import { ref, onMounted, onBeforeUnmount } from "vue"
import { useShepherd } from "vue-shepherd"
import { useTourStore } from "@/stores/tourStore"
import { TourConfig } from "@/types/tour"
import { waitForElements, extractTourSelectors } from "@/utils/domUtils"
import { toursCanBeResetTour } from "@/tours/toursCanBeResetTour"
import { collectMessage } from "@utils/errors"
import { useRouter } from "vue-router"
import type { RouteParamsRaw } from "vue-router"
import { setTourNavigator, setRouteChecker } from "@/services/tourNavigationService"

/**
 * Composable for managing a tour with vue-shepherd
 * @param tourConfig - The tour configuration
 * @returns Object with methods to control the tour
 */
export function useTour(tourConfig: TourConfig) {
   // Get the router instance using the Vue Router composable
   const router = useRouter()
   const tourStore = useTourStore()
   const pendingTourTimeoutId = ref<number | null>(null)
   const tour = useShepherd({
      useModalOverlay: true,
      // Add default step transition options
      defaultStepOptions: {
         // Add smooth transitions
         classes: "shepherd-theme-default shepherd-has-animation",
         scrollTo: { behavior: "smooth", block: "center" },
         // Add transition animation
         when: {
            show: () => {
               const currentStep = tour.shepherd?.getCurrentStep()
               if (currentStep) {
                  const element = currentStep.el
                  if (element) {
                     element.style.opacity = "0"
                     setTimeout(() => {
                        element.style.transition = "opacity 0.3s ease-in-out"
                        element.style.opacity = "1"
                     }, 50)
                  }
               }
            },
         },
         ...tourConfig.options?.defaultStepOptions,
      },
      ...tourConfig.options,
   })
   const isActive = ref(false)

   // Initialize the tour with the provided configuration
   /**
    * Helper function to navigate using router
    * @param routeName The name of the route to navigate to
    * @param params Optional route parameters
    */
   const navigateToRoute = (routeName: string, params?: RouteParamsRaw) => {
      if (router) {
         const currentParams = router.currentRoute.value.params
         return router.push({
            name: routeName,
            params: params || currentParams
         })
      } else {
         console.warn('Router not available for navigation in tour step')
         return Promise.reject('Router not available')
      }
   }

   /**
    * Helper function to check if current route matches the specified route name
    * @param routeName The name of the route to check
    * @returns true if the current route matches the specified route name
    */
   const checkCurrentRoute = (routeName: string): boolean => {
      if (router) {
         return router.currentRoute.value.name === routeName
      } else {
         console.warn('Router not available for route checking in tour step')
         return false
      }
   }

   const initTour = () => {
      // Make the router navigation function available via the reactive tour navigation service
      // This avoids mutating the window object and follows Vue 3 reactivity patterns
      setTourNavigator(navigateToRoute)
      setRouteChecker(checkCurrentRoute)
      // Determine if the tour is skippable (default to true if not specified)
      const isSkippable = tourConfig.skippable !== false

      // Add steps to the tour
      tourConfig.steps.forEach((step, index, stepsArray) => {
         // Add a beforeShowPromise to each step if not already present
         if (!step.beforeShowPromise) {
            const originalStep = { ...step }
            step.beforeShowPromise = () => {
               return new Promise((resolve) => {
                  // If the step has an element selector, wait for it to exist
                  if (
                     originalStep.attachTo &&
                     typeof originalStep.attachTo.element === "string"
                  ) {
                     waitForElements([originalStep.attachTo.element], 2000)
                        .then(() => resolve())
                        .catch(() => {
                           console.warn(
                              `Element not found for tour step: ${originalStep.id}`,
                           )
                           resolve()
                        })
                  } else {
                     // No element to wait for, resolve immediately
                     resolve()
                  }
               })
            }
         }

         // If the tour is skippable and this is not the last step, add a "Skip Tour" button
         if (isSkippable && index < stepsArray.length - 1) {
            // Clone the buttons array if it exists, or create a new one
            const buttons = step.buttons ? [...step.buttons] : []

            // Check if a "Skip Tour" button already exists
            const skipButtonExists = buttons.some(
               (button) =>
                  button.text === "Skip Tour" ||
                  (button.text === "Skip" &&
                     button.action?.toString().includes("markTourAsViewed")),
            )

            // Only add a "Skip Tour" button if it doesn't already exist
            if (!skipButtonExists) {
               // Add the "Skip Tour" button at the beginning of the buttons array
               buttons.unshift({
                  action: function () {
                     if (this.complete) this.complete()

                     // Show the skip notification tour that highlights the settings icon
                     setTimeout(() => {
                        // Create a new Shepherd instance for the skip notification
                        const skipTour = useShepherd(
                           toursCanBeResetTour.options,
                        )

                        // Add the step to the tour
                        toursCanBeResetTour.steps.forEach((step) => {
                           skipTour.addStep(step)
                        })

                        // Start the tour
                        skipTour.start()
                     }, 300) // Short delay to ensure the original tour is closed

                     return undefined
                  },
                  classes: "shepherd-button-secondary q-ml-sm",
                  text: "Skip Tour",
               })

               // Update the step's buttons
               step.buttons = buttons
            }
         }

         tour.addStep(step)
      })

      // Set up event listeners
      tour.on("start", () => {
         isActive.value = true
         tourStore.startTour(tourConfig.id)
      })

      tour.on("complete", () => {
         isActive.value = false
         tourStore.markTourAsViewed(tourConfig.id)
         tourStore.endTour(tourConfig.id)
      })

      tour.on("cancel", () => {
         isActive.value = false
         tourStore.markTourAsViewed(tourConfig.id)
         tourStore.endTour(tourConfig.id)
      })
   }

   // Start the tour
   const startTour = () => {
      // Only start if no tour is active or if this tour is already active
      if (!tour.isActive() && tourStore.startTour(tourConfig.id)) {
         tour.start()
         return true
      }
      return false
   }

   // Stop the tour
   const stopTour = () => {
      if (tour.isActive()) {
         tourStore.endTour(tourConfig.id)
         tour.cancel()
         return true
      }
      return false
   }

   // Reset the tour so it can be viewed again
   const resetTour = () => {
      tourStore.resetTour(tourConfig.id)
   }

   // Check if the tour has been viewed
   const hasBeenViewed = () => {
      return tourStore.hasTourBeenViewed(tourConfig.id)
   }

   // Auto-start the tour if it hasn't been viewed and no other tour is active
   const autoStartTour = async () => {
      // Check if tour has been viewed asynchronously
      const viewed = await tourStore.hasTourBeenViewed(tourConfig.id)

      // Don't start if another tour is already active
      if (
         tourStore.isAnyTourActive() &&
         tourStore.getActiveTourId() !== tourConfig.id
      ) {
         console.log(
            `Cannot start tour ${
               tourConfig.id
            }, another tour is already active: ${tourStore.getActiveTourId()}`,
         )
         collectMessage("Cannot start tour, another tour is already active")
         return false
      }

      if (!viewed) {
         try {
            // Extract all selectors from tour steps
            const selectors = extractTourSelectors(tourConfig.steps)

            // Wait for all tour target elements to be available in the DOM
            const shouldWaitForElements = tourConfig.waitForElements !== false // Default to true if not specified
            if (shouldWaitForElements && selectors.length > 0) {
               // Wait for all selectors with a longer timeout (5000ms)
               await waitForElements(selectors, 5000).catch((error) => {
                  console.warn(
                     "Not all tour elements are available:",
                     error.message,
                  )
                  collectMessage("Not all tour elements are available")
               })
            }

            // Additional delay to ensure DOM is fully rendered and styled
            // Clear any existing timeout
            if (pendingTourTimeoutId.value !== null) {
               clearTimeout(pendingTourTimeoutId.value)
            }

            // Store the new timeout ID and register with tour store
            pendingTourTimeoutId.value = setTimeout(() => {
               pendingTourTimeoutId.value = null
               // Remove timeout from store since it's completed
               tourStore.registerPendingTimeout(tourConfig.id, -1)

               if (tourStore.startTour(tourConfig.id)) {
                  startTour()
                  return true
               }
               return false
            }, 1000) as unknown as number

            // Register timeout with global tour store
            tourStore.registerPendingTimeout(
               tourConfig.id,
               pendingTourTimeoutId.value,
            )
         } catch (error) {
            console.error("Error starting tour:", error)
            collectMessage("Error starting tour")
         }
      }
   }

   onMounted(() => {
      initTour()

      // Register the tour with the global tour store when created
      if (tour.shepherd) {
         tourStore.registerTourInstance(tourConfig.id, tour.shepherd)
      }
   })

   onBeforeUnmount(() => {
      // Clean up any pending tour start
      if (pendingTourTimeoutId.value !== null) {
         clearTimeout(pendingTourTimeoutId.value)
         pendingTourTimeoutId.value = null
      }

      // Clean up by stopping the tour if it's active when the component unmounts
      if (tour.shepherd?.isActive()) {
         tourStore.endTour(tourConfig.id)
         tour.shepherd.cancel()
      }

      // Unregister from global tour store
      tourStore.unregisterTourInstance(tourConfig.id)
   })

   return {
      tour: tour.shepherd,
      isActive,
      startTour,
      stopTour,
      resetTour,
      hasBeenViewed,
      autoStartTour,
      navigateToRoute
   }
}
